import React, { useEffect, useMemo, useState } from "react";
import { TrendingUp, Package, ShoppingBag, DollarSign, BarChart3, Calendar } from "lucide-react";
import AdminLayout from "../../components/AdminLayout";
import { api } from "../../lib/api";

const TABS = [
  { id: "size", label: "By Size" },
  { id: "product", label: "By Product" },
  { id: "month", label: "Month-wise" },
  { id: "year", label: "Year-wise" },
  { id: "revenue", label: "Revenue / Day" },
];

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

function Bar({ value, max, label, sub, accent = "#CFF048" }) {
  const w = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <div className="mb-2">
      <div className="flex justify-between items-baseline label-mono mb-1">
        <span className="text-white truncate">{label}</span>
        <span className="text-[#71717A]">{sub}</span>
      </div>
      <div className="h-3 bg-[#0A0A0A] border border-[#27272A]">
        <div className="h-full" style={{ width: `${w}%`, background: accent }} />
      </div>
    </div>
  );
}

export default function AdminReports() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("size");
  const [metric, setMetric] = useState("revenue"); // revenue | units

  useEffect(() => {
    api.get("/admin/reports").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  const monthMonth = useMemo(() => {
    if (!data) return [];
    const fmt = new Intl.DateTimeFormat("en-IN", { month: "short", year: "numeric" });
    return data.by_month.map((m) => ({ ...m, label: fmt.format(new Date(`${m.key}-01`)) }));
  }, [data]);

  return (
    <AdminLayout title="Reports">
      {loading ? (
        <p className="label-mono text-[#71717A]">// CRUNCHING NUMBERS...</p>
      ) : !data ? (
        <p className="label-mono text-[#71717A]">// NO DATA</p>
      ) : (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="reports-kpis">
            {[
              { label: "REVENUE (PAID)", value: inr(data.summary.revenue), icon: DollarSign },
              { label: "ORDERS", value: data.summary.orders, icon: ShoppingBag },
              { label: "UNITS SOLD", value: data.summary.units, icon: Package },
              { label: "AVG ORDER VALUE", value: inr(data.summary.avg_order_value), icon: TrendingUp },
            ].map((s) => (
              <div key={s.label} className="bg-[#0A0A0A] border border-[#27272A] p-5">
                <div className="flex justify-between items-start">
                  <p className="label-mono text-[#71717A]">{s.label}</p>
                  <s.icon size={16} className="text-[#CFF048]" />
                </div>
                <p className="font-display font-black text-2xl lg:text-3xl mt-3 text-white">{s.value}</p>
              </div>
            ))}
          </div>

          {/* Tabs + metric toggle */}
          <div className="border border-[#27272A] mb-4 flex items-center justify-between flex-wrap gap-3 px-3 py-2 bg-[#0A0A0A]">
            <div className="flex flex-wrap gap-1">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`label-mono px-3 py-2 border transition-colors ${tab === t.id ? "bg-[#CFF048] text-black border-[#CFF048]" : "border-transparent hover:border-[#27272A]"}`}
                  data-testid={`report-tab-${t.id}`}
                >{t.label}</button>
              ))}
            </div>
            <div className="flex gap-2">
              {["revenue", "units"].map((m) => (
                <button
                  key={m}
                  onClick={() => setMetric(m)}
                  className={`label-mono px-3 py-1.5 border ${metric === m ? "border-[#CFF048] text-[#CFF048]" : "border-[#27272A] text-[#71717A]"}`}
                  data-testid={`report-metric-${m}`}
                >BY {m.toUpperCase()}</button>
              ))}
            </div>
          </div>

          {/* TAB content */}
          {tab === "size" && <BySizeView data={data} metric={metric} />}
          {tab === "product" && <ByProductView data={data} metric={metric} />}
          {tab === "month" && <ByPeriodView rows={monthMonth} metric={metric} label="MONTH" />}
          {tab === "year" && <ByPeriodView rows={data.by_year.map((y) => ({ ...y, label: y.key }))} metric={metric} label="YEAR" />}
          {tab === "revenue" && <DailyRevenueView data={data} />}
        </>
      )}
    </AdminLayout>
  );
}

function BySizeView({ data, metric }) {
  const rows = data.by_size;
  const max = Math.max(...rows.map((r) => r[metric]), 1);
  const sumMetric = rows.reduce((s, r) => s + r[metric], 0) || 1;
  return (
    <div className="bg-[#0A0A0A] border border-[#27272A] p-5" data-testid="report-by-size">
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 size={16} className="text-[#CFF048]" />
        <p className="label-mono text-[#CFF048]">// SIZE BREAKDOWN ({metric.toUpperCase()})</p>
      </div>
      {rows.length === 0 && <p className="label-mono text-[#71717A]">// NO PAID ORDERS YET</p>}
      {rows.map((r) => {
        const value = r[metric];
        const pct = ((value / sumMetric) * 100).toFixed(1);
        return (
          <Bar
            key={r.key}
            label={`SIZE ${r.key}`}
            value={value}
            max={max}
            sub={metric === "revenue" ? `${inr(value)}  ·  ${pct}%` : `${value} units  ·  ${pct}%`}
          />
        );
      })}
    </div>
  );
}

function ByProductView({ data, metric }) {
  const rows = data.by_product;
  const max = Math.max(...rows.map((r) => r[metric]), 1);
  return (
    <div className="bg-[#0A0A0A] border border-[#27272A]" data-testid="report-by-product">
      <div className="px-5 py-4 border-b border-[#27272A] flex items-center gap-2">
        <Package size={16} className="text-[#CFF048]" />
        <p className="label-mono text-[#CFF048]">// PRODUCT / ARTICLE BREAKDOWN ({metric.toUpperCase()})</p>
      </div>
      {rows.length === 0 ? (
        <p className="label-mono text-[#71717A] p-5">// NO PAID ORDERS YET</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-[#27272A]">
              <tr className="label-mono text-[#71717A]">
                <th className="text-left p-4">RANK</th>
                <th className="text-left p-4">PRODUCT</th>
                <th className="text-left p-4">UNITS</th>
                <th className="text-left p-4">REVENUE</th>
                <th className="text-left p-4 w-1/3">SHARE</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const value = r[metric];
                const w = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
                return (
                  <tr key={r.product_id || i} className="border-b border-[#18181B]">
                    <td className="p-4 font-mono-cult text-[#71717A]">#{i + 1}</td>
                    <td className="p-4 font-display font-bold flex items-center gap-3">
                      {r.image && <img src={r.image} alt="" className="w-10 h-10 object-cover border border-[#27272A]" />}
                      <span className="truncate max-w-[280px]">{r.name}</span>
                    </td>
                    <td className="p-4 font-mono-cult">{r.units}</td>
                    <td className="p-4 font-mono-cult text-[#CFF048]">{inr(r.revenue)}</td>
                    <td className="p-4">
                      <div className="h-2 bg-[#0A0A0A] border border-[#27272A]">
                        <div className="h-full bg-[#CFF048]" style={{ width: `${w}%` }} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ByPeriodView({ rows, metric, label }) {
  const max = Math.max(...rows.map((r) => r[metric]), 1);
  return (
    <div className="bg-[#0A0A0A] border border-[#27272A] p-5" data-testid={`report-by-${label.toLowerCase()}`}>
      <div className="flex items-center gap-2 mb-4">
        <Calendar size={16} className="text-[#CFF048]" />
        <p className="label-mono text-[#CFF048]">// {label}-WISE BREAKDOWN ({metric.toUpperCase()})</p>
      </div>
      {rows.length === 0 && <p className="label-mono text-[#71717A]">// NO PAID ORDERS YET</p>}
      {rows.map((r) => (
        <Bar
          key={r.key}
          label={r.label || r.key}
          value={r[metric]}
          max={max}
          sub={metric === "revenue"
            ? `${inr(r.revenue)}  ·  ${r.orders} orders  ·  ${r.units} units`
            : `${r.units} units  ·  ${r.orders} orders  ·  ${inr(r.revenue)}`}
        />
      ))}
    </div>
  );
}

function DailyRevenueView({ data }) {
  const rows = data.by_day.slice(-30); // last 30 days
  const max = Math.max(...rows.map((r) => r.revenue), 1);
  return (
    <div className="bg-[#0A0A0A] border border-[#27272A] p-5" data-testid="report-revenue-daily">
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp size={16} className="text-[#CFF048]" />
        <p className="label-mono text-[#CFF048]">// REVENUE TIMELINE — LAST {rows.length} DAYS</p>
      </div>
      {rows.length === 0 ? (
        <p className="label-mono text-[#71717A]">// NO PAID ORDERS YET</p>
      ) : (
        <>
          <div className="flex items-end gap-1 h-48 border-b border-[#27272A] pb-1">
            {rows.map((r) => {
              const h = Math.max(2, Math.round((r.revenue / max) * 180));
              return (
                <div key={r.key} className="flex-1 min-w-[6px] group relative" title={`${r.key}: ${inr(r.revenue)}`}>
                  <div className="bg-[#CFF048] hover:bg-white transition-colors" style={{ height: `${h}px` }} />
                  <div className="opacity-0 group-hover:opacity-100 absolute -top-7 left-1/2 -translate-x-1/2 label-mono text-[10px] text-[#CFF048] whitespace-nowrap pointer-events-none">{inr(r.revenue)}</div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-between label-mono text-[#71717A] mt-2">
            <span>{rows[0]?.key}</span>
            <span>{rows[rows.length - 1]?.key}</span>
          </div>
        </>
      )}
    </div>
  );
}
